import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import LuxButton from "../components/landing/LuxButton";
import { API } from "../App";
import { loadMetaPixel } from "../utils/metaPixel";

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMetaPixel();
  }, []);

  useEffect(() => {
    const techToken = localStorage.getItem("tech_token");
    const techUser = localStorage.getItem("tech_user");
    if (techToken && techUser) {
      navigate("/tech/dashboard", { replace: true });
      return;
    }
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    if (token && user) {
      try {
        const parsed = JSON.parse(user);
        if (parsed.role === "technician") navigate("/tech/dashboard", { replace: true });
        else navigate("/dashboard", { replace: true });
      } catch { /* invalid JSON, stay on login */ }
    }
  }, [navigate]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { token, refresh_token, user } = response.data;

      if (user.role === "technician") {
        localStorage.setItem("tech_token", token);
        if (refresh_token) localStorage.setItem("tech_refresh_token", refresh_token);
        localStorage.setItem("tech_user", JSON.stringify({
          member_id: user.user_id,
          name: user.name,
          email: user.email,
          role: "technician",
          business_user_id: user.owner_id || "",
        }));
        localStorage.setItem("auth_kind", "tech"); // explicit marker for the silent-refresh interceptor
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        toast.success(`Welcome back, ${user.name}!`);
        navigate("/tech/dashboard");
      } else {
        localStorage.setItem("token", token);
        if (refresh_token) localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("auth_kind", "owner"); // explicit marker for the silent-refresh interceptor
        localStorage.removeItem("tech_token");
        localStorage.removeItem("tech_refresh_token");
        localStorage.removeItem("tech_user");
        toast.success("Welcome back!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lux">
      <div className="lux-auth-shell">
        <div className="lux-auth-card" data-testid="login-card">
          <Link to="/" className="flex items-center justify-center mb-7">
            <img src="/logo.png" alt="Fieldz AI" className="h-11 object-contain" />
          </Link>

          <div className="text-center mb-7">
            <h1 className="text-[26px] font-bold text-[var(--lux-text)] mb-1.5" style={{ letterSpacing: "-0.02em" }}>
              Welcome back
            </h1>
            <p className="lux-body text-[14px]">Sign in to your account to continue.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="lux-label">Email</label>
              <div className="relative">
                <Mail className="lux-input-icon" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="lux-input has-icon"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="lux-label" style={{ marginBottom: 0 }}>Password</label>
                <Link to="/forgot-password" className="text-[12px] lux-link-gold" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="lux-input-icon" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="lux-input has-icon"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <LuxButton type="submit" variant="primary" size="lg" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="w-4 h-4" />}
            </LuxButton>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: "var(--lux-border, rgba(255,255,255,0.12))" }} />
            <span className="text-[12px] uppercase tracking-wider" style={{ color: "var(--lux-text-2)" }}>or</span>
            <div className="flex-1 h-px" style={{ background: "var(--lux-border, rgba(255,255,255,0.12))" }} />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button"
            onClick={() => {
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-2.5 h-11 rounded-lg border text-[14px] font-medium transition-colors hover:bg-white/5"
            style={{ borderColor: "var(--lux-border, rgba(255,255,255,0.15))", color: "var(--lux-text-1, inherit)" }}
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-center text-[14px] mt-7" style={{ color: "var(--lux-text-2)" }}>
            Don&apos;t have an account?{" "}
            <Link to="/register" className="lux-link-gold" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
